/**
 * SMS encoding + segment calculation (ARCHITECTURE §7).
 *
 * One SMS credit = one SMS segment.
 *  - GSM-7 (3GPP TS 23.038 default alphabet + extension table):
 *      single message up to 160 septets, concatenated parts carry 153 septets each.
 *      Extension-table characters (e.g. € [ ] { } ^ ~ | \ and form feed) cost 2 septets
 *      (ESC + char) and are never split across parts.
 *  - UCS-2 (any character outside GSM-7, e.g. lowercase Greek, accented Greek, emoji):
 *      single message up to 70 UTF-16 code units, concatenated parts carry 67 each.
 *      Surrogate pairs (emoji) are never split across parts.
 *
 * `toGreekGsmCapitals` converts Greek text to the GSM-7-compatible "Greek capitals without
 * accents" form: Greek letters that exist in GSM-7 (Γ Δ Θ Λ Ξ Π Σ Φ Ψ Ω) are kept, all other
 * Greek capitals are mapped to their Latin look-alikes (Α→A, Β→B, Ε→E, …).
 */

/** GSM 03.38 basic character set (ESC 0x1B excluded). */
const GSM7_BASIC_CHARS =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

/** GSM 03.38 extension table (each costs 2 septets: ESC + char). */
const GSM7_EXTENSION_CHARS = '\f^{}\\[~]|€';

const GSM7_BASIC = new Set(Array.from(GSM7_BASIC_CHARS));
const GSM7_EXTENSION = new Set(Array.from(GSM7_EXTENSION_CHARS));

export type SmsEncoding = 'GSM-7' | 'UCS-2';

export const SMS_LIMITS = {
  'GSM-7': { single: 160, multipart: 153 },
  'UCS-2': { single: 70, multipart: 67 },
} as const;

export interface SmsAnalysis {
  encoding: SmsEncoding;
  /** Characters as the user perceives them (code points). */
  characters: number;
  /** Billing units: septets for GSM-7, UTF-16 code units for UCS-2. */
  units: number;
  /** Number of SMS segments (= SMS credits). 0 for empty text. */
  segments: number;
  /** Capacity per segment for the current length (160/153 or 70/67). */
  perSegment: number;
  /** Units still available in the last segment before another segment is needed. */
  remainingInSegment: number;
  /** Distinct characters that forced UCS-2 (empty for GSM-7). */
  nonGsmCharacters: string[];
}

export function isGsm7Char(ch: string): boolean {
  return GSM7_BASIC.has(ch) || GSM7_EXTENSION.has(ch);
}

export function gsm7Septets(ch: string): 0 | 1 | 2 {
  if (GSM7_BASIC.has(ch)) return 1;
  if (GSM7_EXTENSION.has(ch)) return 2;
  return 0;
}

export function isGsm7(text: string): boolean {
  for (const ch of text) if (!isGsm7Char(ch)) return false;
  return true;
}

export function detectEncoding(text: string): SmsEncoding {
  return isGsm7(text) ? 'GSM-7' : 'UCS-2';
}

/** Pack items of given widths into segments of `capacity` without splitting an item. */
function packSegments(widths: number[], capacity: number): { segments: number; lastFill: number } {
  let segments = 1;
  let fill = 0;
  for (const w of widths) {
    if (fill + w > capacity) {
      segments += 1;
      fill = 0;
    }
    fill += w;
  }
  return { segments, lastFill: fill };
}

export function analyzeSms(text: string): SmsAnalysis {
  const chars = Array.from(text);
  const encoding = detectEncoding(text);
  const nonGsm =
    encoding === 'UCS-2' ? Array.from(new Set(chars.filter((c) => !isGsm7Char(c)))) : [];
  // Widths per character in billing units.
  const widths =
    encoding === 'GSM-7' ? chars.map((c) => gsm7Septets(c) as number) : chars.map((c) => c.length); // UTF-16 code units (surrogate pair = 2)
  const units = widths.reduce((a, b) => a + b, 0);
  const limits = SMS_LIMITS[encoding];

  if (units === 0) {
    return {
      encoding,
      characters: 0,
      units: 0,
      segments: 0,
      perSegment: limits.single,
      remainingInSegment: limits.single,
      nonGsmCharacters: nonGsm,
    };
  }
  if (units <= limits.single) {
    return {
      encoding,
      characters: chars.length,
      units,
      segments: 1,
      perSegment: limits.single,
      remainingInSegment: limits.single - units,
      nonGsmCharacters: nonGsm,
    };
  }
  const { segments, lastFill } = packSegments(widths, limits.multipart);
  return {
    encoding,
    characters: chars.length,
    units,
    segments,
    perSegment: limits.multipart,
    remainingInSegment: limits.multipart - lastFill,
    nonGsmCharacters: nonGsm,
  };
}

/** Number of SMS credits a single message to one recipient costs. */
export function smsSegments(text: string): number {
  return analyzeSms(text).segments;
}

// ─── Greek → GSM-7 capitals ────────────────────────────────────────────────

/** Greek capital (accent-free) → GSM-7 character (Greek where GSM has it, Latin look-alike otherwise). */
const GREEK_CAPITAL_TO_GSM: Record<string, string> = {
  Α: 'A',
  Β: 'B',
  Γ: 'Γ',
  Δ: 'Δ',
  Ε: 'E',
  Ζ: 'Z',
  Η: 'H',
  Θ: 'Θ',
  Ι: 'I',
  Κ: 'K',
  Λ: 'Λ',
  Μ: 'M',
  Ν: 'N',
  Ξ: 'Ξ',
  Ο: 'O',
  Π: 'Π',
  Ρ: 'P',
  Σ: 'Σ',
  Τ: 'T',
  Υ: 'Y',
  Φ: 'Φ',
  Χ: 'X',
  Ψ: 'Ψ',
  Ω: 'Ω',
};

/** Typographic characters commonly produced by phones/word processors → GSM-7 equivalents. */
const TYPOGRAPHY_TO_GSM: Record<string, string> = {
  '\u2018': "'", // ‘
  '\u2019': "'", // ’
  '\u201A': "'", // ‚
  '\u201B': "'", // ‛
  '\u00B4': "'", // ´
  '`': "'",
  '\u201C': '"', // “
  '\u201D': '"', // ”
  '\u201E': '"', // „
  '\u201F': '"', // ‟
  '\u00AB': '"', // «
  '\u00BB': '"', // »
  '\u2010': '-',
  '\u2011': '-',
  '\u2012': '-',
  '\u2013': '-', // –
  '\u2014': '-', // —
  '\u2015': '-', // ―
  '\u2212': '-', // −
  '\u2026': '...', // …
  '\u00A0': ' ', // NBSP
  '\u2007': ' ',
  '\u2009': ' ',
  '\u200A': ' ',
  '\u202F': ' ',
  '\t': ' ',
  '\u037E': ';', // Greek question mark (looks like ;)
  '\u0387': ',', // Greek ano teleia
  '\u00B7': ',', // middle dot (NFD of ano teleia)
  '\u2022': '-', // bullet
};

const COMBINING_MARKS = /[\u0300-\u036f]/g;

function isGreekLetter(ch: string): boolean {
  const cp = ch.codePointAt(0) ?? 0;
  const inGreekBlock = (cp >= 0x0370 && cp <= 0x03ff) || (cp >= 0x1f00 && cp <= 0x1fff);
  return inGreekBlock && /\p{L}/u.test(ch);
}

export interface GreekGsmOptions {
  /** Replace smart quotes, dashes, ellipsis, NBSP … with GSM-7 equivalents. Default true. */
  replaceTypography?: boolean;
  /** Strip accents from non-GSM Latin letters (á → a) when the result is GSM-7. Default true. */
  stripLatinAccents?: boolean;
}

/**
 * Convert text to Greek capitals without accents using only GSM-7 characters where possible.
 * Non-Greek GSM-7 text is preserved as-is. Characters that cannot be represented (e.g. emoji)
 * are left untouched — `analyzeSms` will still report UCS-2 for them.
 */
export function toGreekGsmCapitals(text: string, options: GreekGsmOptions = {}): string {
  const { replaceTypography = true, stripLatinAccents = true } = options;
  let out = '';
  for (const ch of text) {
    if (GSM7_BASIC.has(ch) || GSM7_EXTENSION.has(ch)) {
      // Already GSM-7. Greek capitals Γ Δ Θ … live in the basic set; keep everything else.
      out += ch;
      continue;
    }
    if (isGreekLetter(ch)) {
      const base = ch.normalize('NFD').replace(COMBINING_MARKS, '');
      let mapped = '';
      for (const b of base) {
        const upper = b === 'ς' ? 'Σ' : b.toUpperCase();
        mapped += GREEK_CAPITAL_TO_GSM[upper] ?? (isGsm7(upper) ? upper : b);
      }
      out += mapped;
      continue;
    }
    if (replaceTypography && ch in TYPOGRAPHY_TO_GSM) {
      out += TYPOGRAPHY_TO_GSM[ch];
      continue;
    }
    if (stripLatinAccents) {
      const stripped = ch.normalize('NFD').replace(COMBINING_MARKS, '');
      if (stripped !== ch && stripped.length > 0 && isGsm7(stripped)) {
        out += stripped;
        continue;
      }
    }
    out += ch;
  }
  return out;
}

/** Savings preview for the composer's "convert to Greek capitals" suggestion. */
export function greekCapitalsSavings(text: string): {
  original: SmsAnalysis;
  converted: SmsAnalysis;
  convertedText: string;
  segmentsSaved: number;
} {
  const original = analyzeSms(text);
  const convertedText = toGreekGsmCapitals(text);
  const converted = analyzeSms(convertedText);
  return {
    original,
    converted,
    convertedText,
    segmentsSaved: Math.max(0, original.segments - converted.segments),
  };
}
